import { useState, useEffect } from 'react';
import { EmailStep } from './EmailStep';
import { PricingStep, type PlanTier } from './PricingStep';
import { CheckoutStep, type MemberData } from './CheckoutStep';
import { SuccessStep } from './SuccessStep';
import { detectCountry, isIndiaCountry } from '../lib/geoLocation';
import { supabase } from '../lib/supabase';

type Step = 'email' | 'pricing' | 'checkout';

export function SignupFlow() {
    const [currentStep, setCurrentStep] = useState<Step>('email');
    const [email, setEmail] = useState('');
    const [selectedPlan, setSelectedPlan] = useState<PlanTier | null>(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [countryCode, setCountryCode] = useState<string>('IN');
    const [isInternational, setIsInternational] = useState(false);

    // Auto-detect country on mount
    useEffect(() => {
        detectCountry().then((geo) => {
            setCountryCode(geo.countryCode);
            setIsInternational(!geo.isIndia);
        });
    }, []);

    // Update isInternational when country changes
    const handleCountryChange = (newCountryCode: string) => {
        setCountryCode(newCountryCode);
        setIsInternational(!isIndiaCountry(newCountryCode));
    };

    const handleEmailSubmit = async (submittedEmail: string) => {
        setEmail(submittedEmail);

        try {
            const { error } = await supabase
                .from('leads')
                .insert({ email: submittedEmail });

            if (error) {
                console.error('Error saving lead:', error);
            }
        } catch (err) {
            console.error('Failed to save lead:', err);
        }

        setCurrentStep('pricing');
    };

    const handlePlanSelect = (plan: PlanTier) => {
        setSelectedPlan(plan);
        setCurrentStep('checkout');
    };

    const handleChangeTier = () => {
        setCurrentStep('pricing');
    };

    const handleBack = () => {
        setCurrentStep('email');
    };

    const handleCheckoutSubmit = async (paymentId: string, memberData: MemberData) => {
        console.log('Checkout successful with Payment ID:', paymentId);

        try {
            const { error } = await supabase
                .from('members')
                .insert({
                    email: email,
                    first_name: memberData.firstName,
                    last_name: memberData.lastName,
                    phone: memberData.phone,
                    dob: memberData.dob,
                    address: memberData.address,
                    zip_code: memberData.zipCode,
                    plan_name: selectedPlan?.name,
                    amount_paid: memberData.amountPaid,
                    payment_id: paymentId,
                    // order_id is optional or can be added if available from razorpay response
                });

            if (error) {
                console.error('Error saving member:', error);
                // Even if saving to DB fails, we probably still want to show success if payment worked?
                // Or maybe show an error? For now, logging and proceeding.
            }
        } catch (err) {
            console.error('Failed to save member:', err);
        }

        setShowSuccess(true);
    };

    if (showSuccess) {
        return (
            <SuccessStep
                planName={selectedPlan?.name || 'Member'}
                onReset={() => {
                    setShowSuccess(false);
                    setCurrentStep('email');
                    setEmail('');
                    setSelectedPlan(null);
                }}
            />
        );
    }

    return (
        <>
            {currentStep === 'email' && <EmailStep onSubmit={handleEmailSubmit} />}

            {currentStep === 'pricing' && (
                <PricingStep
                    onSelectPlan={handlePlanSelect}
                    onClose={handleBack}
                    isInternational={isInternational}
                />
            )}

            {currentStep === 'checkout' && selectedPlan && (
                <CheckoutStep
                    email={email}
                    plan={selectedPlan}
                    onBack={() => setCurrentStep('pricing')}
                    onChangeTier={handleChangeTier}
                    onSubmit={handleCheckoutSubmit}
                    countryCode={countryCode}
                    isInternational={isInternational}
                    onCountryChange={handleCountryChange}
                />
            )}
        </>
    );
}
